<template>

  <OpenOrCloseSummary v-if="summaryOnly"
                      :summary-link="summaryLink"
                      :opening-message="openingMessage"
                      :closing-message="closingMessage"
                      :opening-hours="openingHours"
                      :opening-hours-override="openingHoursOverride" />

  <div v-else>

    <button v-show="!isOpen"
            aria-label="Open opening hours"
            title="Opening hours"
            @click="openPanel"
            class="open-or-close-trigger cursor-pointer">
      <svg width="22"
           height="22"
           aria-hidden="true"
           class="open-or-close-trigger__icon">
        <use :xlink:href="spriteUrl('clock-light-icon')" />
      </svg>
    </button>

    <Transition name="oc-fade">
      <div v-if="isOpen"
           @click="close"
           class="open-or-close-backdrop" />
    </Transition>

    <div ref="boxRef"
         :style="dragStyle"
         @mousedown="onBoxMouseDown"
         class="open-or-close-box"
         :class="{ 
            'is-open': isOpen,
            'is-dragging': isDragging 
          }">
      <div class="open-or-close">

        <div class="open-or-close-heading-box">

          <h2 v-if="heading"
              class="open-or-close-title">
            {{ heading }}
          </h2>

          <div class="open-or-close-current_status"
               :class="{ 'is-closed': isCurrentlyClosed }">

            <template v-if="isCurrentlyClosed">
              {{ closedMessage }}

              <template v-if="nextOpenDisplay?.opens">
                {{ formatTime(nextOpenDisplay.opens) }}
              </template>
            </template>

            <template v-else>
              Opens: {{ formatTime(todayDisplay?.opens) }}
            </template>

          </div>

          <button aria-label="Close opening hours"
                  @click="close"
                  class="open-or-close-close-btn cursor-pointer"
                  title="Close opening hours">
            <svg width="27"
                 height="27"
                 aria-hidden="true">
              <use :xlink:href="spriteUrl('icon-close')" />
            </svg>
          </button>

        </div>

        <ul v-if="displaySchedule.length > 0"
            class="open-or-close-list">
          <li v-for="item in displaySchedule"
              :key="item.displayName"
              :class="[
                'open-or-close-item',
                { 'is-active': item.isToday }
              ]">

            <strong>
              {{ item.displayName }}:
            </strong>

            <div>

              <template v-if="!item.isClosed">

                <template v-if="item.label">
                  {{ item.label }} -
                </template>

                {{ formatTime(item.opens) }} - {{ formatTime(item.closes) }}

              </template>

              <span v-else>
                {{ item.label || 'Closed' }}
              </span>

            </div>

          </li>
        </ul>

        <div v-if="openingAnnouncement"
             v-html="openingAnnouncement"
             class="open-or-close-holidays_message"></div>

      </div>
    </div>

  </div>

</template>

<script setup lang="ts">
  import OpenOrCloseSummary from './OpenOrCloseSummary.vue';
  import { computed, ref } from 'vue';
  import {
    useOpeningHours,
    formatTimeAmPm as formatTime,
    formatClosingMessage,
    isPastClosingTime,
    type OpeningHoursItem,
    type OpeningHoursOverride,
  } from '../../modules/utils/useOpeningHours';
  import { useOpenOrClosePanel } from '../utils/useOpenOrClosePanel';
  import { spriteUrl } from '../utils/spriteUrl';
  import { useDraggable } from '../utils/useDraggable';

  interface Props {
    heading?: string;
    openingMessage?: string;
    closingMessage?: string;
    openingHours?: OpeningHoursItem[];
    openingHoursOverride?: OpeningHoursOverride[];
    openingAnnouncement?: string;
    summaryOnly?: boolean;
    summaryLink?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    heading: '',
    openingMessage: '',
    closingMessage: '',
    openingHours: () => [],
    openingHoursOverride: () => [],
    summaryOnly: false,
    summaryLink: '',
    openingAnnouncement: '',
  });

  const { displaySchedule, todayDisplay, nextOpenDisplay } = useOpeningHours(
    () => props.openingHours,
    () => props.openingHoursOverride,
  );

  const { isOpen, openPanel, close } = useOpenOrClosePanel();
  const { isDragging, dragStyle, startDrag } = useDraggable();
  const boxRef = ref<HTMLElement | null>(null);

  function onBoxMouseDown(e: MouseEvent) {
    if (boxRef.value) startDrag(e, boxRef.value);
  }

  const isCurrentlyClosed = computed(() =>
    !todayDisplay.value ||
    todayDisplay.value.isClosed ||
    isPastClosingTime(todayDisplay.value.closes)
  );

  const closedMessage = computed(() =>
    formatClosingMessage(todayDisplay.value?.message || props.closingMessage || 'Closed', nextOpenDisplay.value)
  );
</script>
